import { Select } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  RENDER_PRESET_FIT_OUTPUT_SIZE,
  RENDER_PRESET_MAX_PAGE_SIZE,
  RENDER_PRESET_OUTPUT_SIZES,
} from '../constants';
import { shortsRenderPresetService } from '../services/shortsRenderPresetService';
import { ShortsRenderPresetInterface } from '../types/shortsRenderPresetType';

interface RenderPresetSelectProps {
  /** Supplied by Form.Item. `null`/`undefined` means no preset is linked. */
  value?: number | null;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** "1080×1440, crop 3:4" — enough to tell two presets apart in a dropdown. */
export const describeRenderPreset = (preset: ShortsRenderPresetInterface): string => {
  const output =
    preset.fit_mode === 'crop' && preset.crop_ratio
      ? RENDER_PRESET_OUTPUT_SIZES[preset.crop_ratio]
      : RENDER_PRESET_FIT_OUTPUT_SIZE;
  const shape = preset.fit_mode === 'crop' ? `crop ${preset.crop_ratio ?? '?'}` : 'fit';
  return `${output}, ${shape}`;
};

/**
 * Render preset picker, shared by the Season edit screen and the convert-to-shorts modal.
 * Options are platform-scoped by the backend, so a cross-platform selection is not
 * reachable from normal UI — a 404 from convert means the preset was deleted meanwhile.
 *
 * Known limit: loads a single page (max 100). Presets beyond that are not listed.
 */
const RenderPresetSelect: React.FC<RenderPresetSelectProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => {
  const [presets, setPresets] = useState<ShortsRenderPresetInterface[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPresets = async () => {
      setLoading(true);
      try {
        const response = await shortsRenderPresetService.fetchPresets(
          new URLSearchParams({ page_size: String(RENDER_PRESET_MAX_PAGE_SIZE) })
        );
        if (!cancelled) {
          setPresets(response.results ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch Shorts render presets:', error);
          setPresets([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPresets();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Select
      allowClear
      showSearch
      optionFilterProp="label"
      loading={loading}
      disabled={disabled}
      placeholder={placeholder ?? 'None'}
      // allowClear yields undefined; normalize to null so the PATCH clears the link.
      value={value ?? undefined}
      onChange={next => onChange?.(next ?? null)}
      options={presets.map(preset => ({
        value: preset.id,
        label: `${preset.name} (${describeRenderPreset(preset)})`,
      }))}
    />
  );
};

export default RenderPresetSelect;
